// Version release timeline (Sheet / detail-page aside): milestone dates
// (editable), release records from /channel-releases and Reports. Channel
// records are read-only here (recorded on the channel page); channel-less apps
// add / delete their version-level records (channelId = 0) right here.
import React, { useState } from "react";
import useSWR from "swr";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/badge/chip";
import { ChannelStateChip } from "@/components/badge/channel-state";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { PickerDate } from "@/components/PickerDate";
import { UserAvatar } from "@/components/user-chip";
import {
  delFetcher,
  mutateRequest,
  optimisticAtom,
  optimisticList,
  postFetcher,
  putFetcher,
  swrKey,
} from "@/lib/swr";
import useSWRMutation from "swr/mutation";
import { useReportDocs, type ReportDocsApi } from "@/hooks/use-report-docs";
import { useUser } from "@/hooks/users";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import {
  ReportDeleteDialog,
  ReportDocLink,
  ReportSummaryEditor,
} from "@/components/reports/report-doc-atoms";
import { ResultBadge } from "@/components/badge/result-badge";
import {
  CircleCheckIcon,
  CirclePlusIcon,
  FileTextIcon,
  RocketIcon,
  RotateCwIcon,
  TrashIcon,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import type { Version, VersionReportDoc } from "@/types/domain";
import { DATE_FULL, withCurrentTime } from "@/lib/date";
import {
  VERSION_MILESTONES,
  type MilestoneField,
} from "@/constants/version-milestones";

// Release record as returned by GET /channel-releases?versionId=
interface ReleaseRecord {
  id: number;
  channelId: number;
  /** Joined channel name; absent on version-level records (channelId = 0). */
  channelName?: string;
  releasedAt: string;
  percent?: number | null;
  state?: string | null;
  /** Operator's user id, resolved server-side (open ids never leave the server). */
  operatorUserId?: string | null;
  operatorName?: string | null;
}

type ReleaseState = "rolling" | "online";

const refreshReleases = (versionId: string) => {
  mutateRequest("/channel-releases");
  mutateRequest("/versions");
  mutateRequest(`/versions/${versionId}`);
};

type TimelineItem =
  | {
      ts: number | null;
      kind: "milestone";
      field: MilestoneField;
      label: string;
      date: Date | null;
    }
  | { ts: number | null; kind: "release"; date: Date; release: ReleaseRecord }
  | { ts: number | null; kind: "report"; time: string; doc: VersionReportDoc };

// Merge milestones + release records + Reports into one timeline: newest
// first; undated milestones sink to the bottom (in milestone order)
const buildTimeline = (
  version: Version,
  releases: ReleaseRecord[],
): TimelineItem[] => {
  const items: TimelineItem[] = [];

  VERSION_MILESTONES.forEach((m) => {
    const v = version[m.field];
    const d = v ? new Date(v) : null;
    items.push({
      ts: d ? d.getTime() : null,
      kind: "milestone",
      field: m.field,
      label: m.label,
      date: d,
    });
  });

  releases.forEach((release) => {
    const d = new Date(release.releasedAt);
    items.push({ ts: d.getTime(), kind: "release", date: d, release });
  });

  (version.reportDocs ?? []).forEach((doc) => {
    const d = doc.createTime ? new Date(doc.createTime) : null;
    const valid = d && !isNaN(d.getTime());
    items.push({
      ts: valid ? (d as Date).getTime() : null,
      kind: "report",
      time: valid ? format(d as Date, DATE_FULL) : "",
      doc,
    });
  });

  return items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => {
      const ta = a.it.ts;
      const tb = b.it.ts;
      if (ta == null && tb == null) return a.i - b.i;
      if (ta == null) return 1;
      if (tb == null) return -1;
      if (ta !== tb) return tb - ta;
      // Same instant: later-pushed (e.g. later-recorded release) on top
      return b.i - a.i;
    })
    .map(({ it }) => it);
};

// Circular marker for each kind of node (color + icon)
const NodeMarker = ({ item }: { item: TimelineItem }) => {
  let color = "border-muted-foreground text-muted-foreground";
  let Icon = FileTextIcon;
  if (item.kind === "milestone") {
    Icon = CircleCheckIcon;
    color = item.date
      ? "border-primary text-primary"
      : "border-muted-foreground/30 text-muted-foreground/30";
  } else if (item.kind === "release") {
    color = "border-primary text-primary";
    Icon = RocketIcon;
  } else {
    Icon = FileTextIcon;
    const r = item.doc?.result;
    if (r === "positive") color = "border-green-500 text-green-600";
    else if (r === "negative") color = "border-destructive text-destructive";
    else color = "border-muted-foreground/40 text-muted-foreground";
  }
  return (
    <span
      className={cn(
        "absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full border bg-background",
        color,
      )}
    >
      <Icon className="h-3 w-3" />
    </span>
  );
};

// Release timeline body: milestones / release records / Report; milestones and Reports are edited in place. Can be embedded in a Sheet or on the right of the detail page.
export const VersionTimelineBody = ({ version }: { version: Version }) => {
  // Same key as the version page's Channels tab — SWR dedupes
  const { data: releases } = useSWR<ReleaseRecord[]>(
    swrKey("/channel-releases", { versionId: version.id }),
  );
  // Channel-less app: version-level records are added / deleted here
  const { data: channels } = useSWR<unknown[]>(
    version.pid ? swrKey("/channels", { pid: version.pid }) : null,
  );
  const channelless = !!channels && channels.length === 0;
  const [removing, setRemoving] = useState<ReleaseRecord | null>(null);
  const isProduction = import.meta.env.PROD;

  const { trigger, isMutating } = useSWRMutation(
    `/versions/${version.id}`,
    putFetcher,
    {
      onSuccess: (data: Version | undefined) => {
        if (data) {
          optimisticList(`/versions`, version.id, data);
          optimisticAtom(`/versions/${version.id}`, data);
        }
      },
    },
  );

  // Milestone date: save on change
  const updateMilestone = (field: MilestoneField, date: Date | null) => {
    if (isMutating) return;
    const utc = date ? date.toISOString() : null;
    const data = { [field]: utc };
    optimisticList(`/versions`, version.id, data);
    optimisticAtom(`/versions/${version.id}`, data);
    trigger(data);
  };

  // ---- Report add / delete / edit (shared hook) ----
  const docs = useReportDocs({
    ownerType: "version",
    ownerId: version.id,
    reportDocs: version.reportDocs || [],
    applyDocs: (next) => {
      optimisticAtom(`/versions/${version.id}`, { reportDocs: next });
      optimisticList("/versions", version.id, { reportDocs: next });
    },
    refresh: () => mutateRequest(`/versions/${version.id}`),
  });

  const timeline = buildTimeline(version, releases ?? []);

  return (
    <div className="flex flex-col">
      {/* Vertical timeline: milestones / release records / Report */}
      <div className="mt-4">
        <ol className="relative ml-3 border-l border-border">
          {timeline.map((e, i) => (
            <li key={i} className="mb-5 ml-6">
              <NodeMarker item={e} />
              {e.kind === "report" ? (
                <ReportNode
                  doc={e.doc}
                  time={e.time}
                  isProduction={isProduction}
                  docs={docs}
                />
              ) : e.kind === "milestone" ? (
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={cn(
                      "text-sm",
                      e.date ? "font-medium" : "text-muted-foreground",
                    )}
                  >
                    {e.label}
                  </span>
                  <PickerDate
                    date={e.date}
                    setDate={(d) => updateMilestone(e.field, d)}
                    isLoading={isMutating}
                  />
                </div>
              ) : (
                <ReleaseNode
                  release={e.release}
                  date={e.date}
                  // Only version-level records are deletable here; channel
                  // records are managed on the channel page
                  onRemove={
                    channelless && e.release.channelId === 0
                      ? () => setRemoving(e.release)
                      : undefined
                  }
                />
              )}
            </li>
          ))}
        </ol>
      </div>

      {channelless && (
        <DeleteReleaseDialog
          versionId={version.id}
          release={removing}
          onClose={() => setRemoving(null)}
        />
      )}
    </div>
  );
};

// One release record: channel name (or "Release"), state, percent, operator
// avatar, date; trash button when deletable.
const ReleaseNode = ({
  release: r,
  date,
  onRemove,
}: {
  release: ReleaseRecord;
  date: Date;
  onRemove?: () => void;
}) => {
  // Unknown / migrated operators (no user id) show nothing
  const operator = useUser(r.operatorUserId);
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="truncate text-sm">{r.channelName ?? "Release"}</span>
      {r.state && <ChannelStateChip state={r.state} />}
      {r.state === "rolling" && r.percent != null && (
        <Chip tone="blue">{r.percent}%</Chip>
      )}
      <span className="ml-auto flex shrink-0 items-center gap-2">
        {operator && (
          <span title={operator.name}>
            <UserAvatar user={operator} className="h-5 w-5" />
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {format(date, DATE_FULL)}
        </span>
        {onRemove && (
          <Button
            variant="ghost"
            size="icon-xs"
            title="Delete this release"
            className="text-muted-foreground hover:text-destructive"
            onClick={onRemove}
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </Button>
        )}
      </span>
    </div>
  );
};

// Record a version-level release: date + rolling (percent 1–100,
// required) or online (no percent; only rolling carries one). Version-state
// gating is the server's call (400 → toast).
// Header action on the version detail page (channel-less apps only).
export const AddReleaseDialog = ({ versionId }: { versionId: string }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | null>(new Date());
  const [state, setState] = useState<ReleaseState>("rolling");
  const [percent, setPercent] = useState("");
  const { trigger, isMutating } = useSWRMutation(
    "/channel-releases",
    postFetcher,
    {
      onSuccess: () => {
        refreshReleases(versionId);
        setOpen(false);
      },
      onError: (error: unknown) =>
        toastError(toast, "Failed to add release", error),
    },
  );
  const openDialog = () => {
    setDate(new Date());
    setState("rolling");
    setPercent("");
    setOpen(true);
  };
  const percentValue = Number(percent.trim());
  const validPercent =
    Number.isInteger(percentValue) && percentValue >= 1 && percentValue <= 100;
  const canSave = !!date && (state !== "rolling" || validPercent);

  return (
    <>
      <Button variant="outline" size="sm" onClick={openDialog}>
        <CirclePlusIcon className="h-3.5 w-3.5" /> Add release
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add release</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-muted-foreground">Date</span>
              <PickerDate date={date} setDate={setDate} />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-muted-foreground">State</span>
              <Select
                value={state}
                onValueChange={(v: string) => setState(v as ReleaseState)}
              >
                <SelectTrigger size="sm" className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rolling">
                    <ChannelStateChip state="rolling" />
                  </SelectItem>
                  <SelectItem value="online">
                    <ChannelStateChip state="online" />
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {state === "rolling" && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground">Percent</span>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={percent}
                  placeholder="50"
                  aria-label="Percent"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPercent(e.target.value)
                  }
                  className="h-8 w-32 tabular-nums"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={isMutating || !canSave}
              onClick={() =>
                date &&
                trigger({
                  versionId,
                  releasedAt: withCurrentTime(date),
                  ...(state === "rolling" ? { percent: percentValue } : {}),
                  state,
                })
              }
            >
              {isMutating ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Confirm-then-DELETE a version-level release (fix mistakes by delete + re-add)
const DeleteReleaseDialog = ({
  versionId,
  release,
  onClose,
}: {
  versionId: string;
  release: ReleaseRecord | null;
  onClose: () => void;
}) => {
  const { toast } = useToast();
  const { trigger, isMutating } = useSWRMutation(
    "/channel-releases",
    delFetcher,
    {
      onSuccess: () => {
        refreshReleases(versionId);
        onClose();
      },
      onError: (error: unknown) =>
        toastError(toast, "Failed to delete release", error),
    },
  );
  return (
    <AlertDialog open={!!release} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this release?</AlertDialogTitle>
          <AlertDialogDescription>
            {release &&
              `${format(new Date(release.releasedAt), DATE_FULL)}${
                release.state === "rolling" && release.percent != null
                  ? ` · ${release.percent}%`
                  : ""
              } will be removed; other records stay.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={isMutating}
            onClick={(e) => {
              // Keep the dialog open until the request settles
              e.preventDefault();
              if (release) trigger(String(release.id));
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Release timeline Sheet: children acts as the custom trigger (shared by the Process / Publish log / Report columns)
export const VersionTimelineSheet = ({
  version,
  children,
}: {
  version: Version;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className="flex w-[460px] flex-col overflow-y-auto sm:max-w-[460px]">
        <SheetHeader>
          <SheetTitle>Release timeline</SheetTitle>
        </SheetHeader>
        <VersionTimelineBody version={version} />
      </SheetContent>
    </Sheet>
  );
};

// A single Report node in the timeline: result label + name + summary + actions
const ReportNode = ({
  doc,
  time,
  isProduction,
  docs,
}: {
  doc: VersionReportDoc;
  time: string;
  isProduction: boolean;
  docs: ReportDocsApi;
}) => {
  const isEditing = docs.editingDocUrl === doc.url;
  return (
    <div>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {doc.result && (
            <ResultBadge result={doc.result} className="whitespace-nowrap" />
          )}
          <ReportDocLink doc={doc} className="font-medium" />
        </div>
        {time && (
          <span className="shrink-0 text-xs text-muted-foreground">{time}</span>
        )}
      </div>
      <div className="mt-1">
        {isEditing ? (
          <ReportSummaryEditor
            value={docs.editingSummary}
            onChange={docs.setEditingSummary}
            onCancel={docs.cancelEditSummary}
            onSave={() => docs.saveSummary(doc)}
            isSaving={docs.isSavingSummary}
          />
        ) : (
          <div className="space-y-1">
            {doc.summary ? (
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {doc.summary}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">No summary.</p>
            )}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => docs.startEditSummary(doc)}
              >
                Edit summary
              </Button>
              {!isProduction && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                  onClick={() => docs.resendWebhook(doc.url)}
                  disabled={docs.resendingDocUrl === doc.url}
                  title="Resend feishu message"
                >
                  <RotateCwIcon
                    className={`h-3 w-3 ${docs.resendingDocUrl === doc.url ? "animate-spin" : ""}`}
                  />
                </Button>
              )}
              <ReportDeleteDialog
                open={docs.deleteUrl === doc.url}
                onCancel={() => docs.askDelete(null)}
                onConfirm={docs.confirmDelete}
                isMutating={docs.isMutating}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                  onClick={() => docs.askDelete(doc.url)}
                  disabled={docs.isMutating}
                >
                  <TrashIcon className="h-3 w-3" />
                </Button>
              </ReportDeleteDialog>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
